if (!customElements.get('bundle-customizer')) {
  customElements.define(
    'bundle-customizer',
    class BundleCustomizer extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        this.toggle = this.querySelector('[data-bundle-toggle]');
        this.panel = this.querySelector('[data-bundle-panel]');
        this.rows = this.querySelector('[data-bundle-rows]');
        this.template = this.querySelector('[data-bundle-row-template]');
        this.listeners = new AbortController();
        this.toggle.addEventListener('click', () => this.setExpanded(this.panel.hidden), {
          signal: this.listeners.signal,
        });
        this.rows.addEventListener('change', (event) => this.changeOption(event), {
          signal: this.listeners.signal,
        });
        this.scheduleRefresh = () => {
          cancelAnimationFrame(this.refreshFrame);
          this.refreshFrame = requestAnimationFrame(() => this.refresh());
        };
        this.observer = new MutationObserver((records) => {
          if (records.some(({ target }) => !this.contains(target))) this.scheduleRefresh();
        });
        this.observer.observe(this.productInfo, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'data-value', 'src', 'disabled'],
        });
        this.refresh();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.listeners?.abort();
        this.widgetListeners?.abort();
        cancelAnimationFrame(this.refreshFrame);
        this.widget?.classList.remove('veldra-bundle-customization-ready');
      }

      getNativeRows() {
        const selectedBar = this.widget?.querySelector('.kaching-bundles__bar--selected');
        return Array.from(selectedBar?.querySelectorAll('.kaching-bundles__bar-variant-selector-wrapper') || []).map(
          (row) => {
            const controls = Array.from(row.querySelectorAll('select[data-name]'));
            return {
              color: controls.find((select) => select.dataset.name.trim().toLowerCase() === 'farve'),
              size: controls.find((select) => select.dataset.name.trim().toLowerCase() === 'størrelse'),
              image: row.querySelector('.kaching-bundles__bar-variant-image')?.getAttribute('src'),
            };
          }
        );
      }

      refresh() {
        const widget = this.productInfo.querySelector('kaching-bundles-block');
        if (widget !== this.widget) {
          this.widget?.classList.remove('veldra-bundle-customization-ready');
          this.widgetListeners?.abort();
          this.widget = widget;
          this.widgetListeners = new AbortController();
          widget?.addEventListener('items-changed', this.scheduleRefresh, { signal: this.widgetListeners.signal });
          widget?.addEventListener('deal-bar-selected', this.scheduleRefresh, { signal: this.widgetListeners.signal });
        }

        const nativeRows = this.getNativeRows();
        const ready = nativeRows.length > 1 && nativeRows.every(({ color, size }) => color && size);
        if (!ready) {
          this.hidden = true;
          this.setExpanded(false);
          if (this.widget?.classList.contains('veldra-bundle-customization-ready')) {
            this.widget.classList.remove('veldra-bundle-customization-ready');
          }
          return;
        }

        if (!this.widget.classList.contains('veldra-bundle-customization-ready')) {
          this.widget.classList.add('veldra-bundle-customization-ready');
        }
        this.hidden = false;

        while (this.rows.children.length > nativeRows.length) this.rows.lastElementChild.remove();
        while (this.rows.children.length < nativeRows.length) this.rows.append(this.template.content.cloneNode(true));

        nativeRows.forEach((nativeRow, index) => {
          const row = this.rows.children[index];
          row.dataset.bundleRow = index;
          row.querySelector('[data-bundle-number]').textContent = index + 1;
          const image = row.querySelector('[data-bundle-image]');
          image.hidden = !nativeRow.image;
          if (nativeRow.image && image.getAttribute('src') !== nativeRow.image) image.setAttribute('src', nativeRow.image);

          for (const field of ['color', 'size']) {
            const nativeSelect = nativeRow[field];
            const select = row.querySelector(`[data-bundle-field="${field}"]`);
            const options = Array.from(nativeSelect.options).map(({ value, textContent, disabled }) => ({
              value,
              label: textContent,
              disabled,
            }));
            const signature = JSON.stringify(options);
            if (select.dataset.optionsSignature !== signature) {
              select.replaceChildren(
                ...options.map(({ value, label, disabled }) => {
                  const option = new Option(label, value);
                  option.disabled = disabled;
                  return option;
                })
              );
              select.dataset.optionsSignature = signature;
            }
            select.value = nativeSelect.value;
            select.disabled = nativeSelect.disabled;
            select.id = `${this.id}-${field}-${index}`;
            select.closest('label').htmlFor = select.id;
          }
        });
      }

      setExpanded(expanded) {
        this.panel.hidden = !expanded;
        this.toggle.setAttribute('aria-expanded', String(expanded));
      }

      changeOption(event) {
        const select = event.target.closest('select[data-bundle-field]');
        if (!select) return;
        const index = Number(select.closest('[data-bundle-row]').dataset.bundleRow);
        const nativeSelect = this.getNativeRows()[index]?.[select.dataset.bundleField];
        const option = Array.from(nativeSelect?.options || []).find(({ value }) => value === select.value);
        if (!nativeSelect || nativeSelect.disabled || !option || option.disabled) {
          this.scheduleRefresh();
          return;
        }
        nativeSelect.value = select.value;
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.scheduleRefresh();
      }
    }
  );
}
